import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import ItemGroupsActions from "../../../store/actions/items/categories/itemGroups/itemGroups.actions";
import { useParams, useNavigate } from "react-router-dom";
import { SkeletonForm } from "../../../components/general/skeleton";
import useDelayedFlag from "../../../hooks/useDelayedFlag";

export const EditCategoryPageComponent = ({ categoryActions }) => {
  const { categoryId } = useParams(); // Extract category ID from the URL
  const navigate = useNavigate();

  const [categoryDetails, setCategoryDetails] = useState({
    id: "",
    categoryName: "",
    description: "",
    imageURL: "",
  });
  const [selectedTab, setSelectedTab] = useState("details"); // Tab state
  const [imageFile, setImageFile] = useState(null); // File for image upload
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const showSkeleton = useDelayedFlag(isLoading);

  // Fetch category details when the component mounts
  useEffect(() => {
    fetchCategoryDetails();
  }, [categoryId]);

  const fetchCategoryDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await categoryActions.getCategoryById(categoryId);
      setCategoryDetails(response.payload);
    } catch (error) {
      console.error("Error fetching category details:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCategoryDetails({ ...categoryDetails, [name]: value });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setImageFile(file);
  };

  const handleUpdateCategory = async (event) => {
    event.preventDefault(); // Prevent default form submission

    try {
      await categoryActions.updateCategory(categoryDetails);
      navigate("/categories/list"); // Navigate back to the categories list
    } catch (error) {
      console.error("Error updating category:", error);
      setError(error);
    }
  };

  const handleUpdateImage = async () => {
    if (!imageFile) {
      alert("Please select an image to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", imageFile);

    try {
      setIsLoading(true);
      await categoryActions.updateCategoryImage(categoryId, formData);
      await fetchCategoryDetails(); // Refresh category details to update the image
      alert("Image updated successfully.");
    } catch (error) {
      console.error("Error updating image:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2>Edit Category</h2>

      {isLoading ? (
        showSkeleton && <SkeletonForm fields={3} />
      ) : error ? (
        <p>Error: {error.message || "Something went wrong"}</p>
      ) : (
        <div>
          {/* Tabs */}
          <div>
            <button onClick={() => setSelectedTab("details")} style={{ fontWeight: selectedTab === "details" ? "bold" : "normal" }}>
              Details
            </button>
            <button onClick={() => setSelectedTab("image")} style={{ fontWeight: selectedTab === "image" ? "bold" : "normal" }}>
              Image
            </button>
          </div>

          {/* Tab Content */}
          {selectedTab === "details" && (
            <form onSubmit={handleUpdateCategory}>
              <div>
                <label>Category Name:</label>
                <input type="text" name="categoryName" value={categoryDetails.categoryName} onChange={handleInputChange} required />
              </div>
              <div>
                <label>Description:</label>
                <input type="text" name="description" value={categoryDetails.description} onChange={handleInputChange} required />
              </div>
              <div>
                <button type="submit">Update Category</button>
                <button type="button" onClick={() => navigate("/categories/list")}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {selectedTab === "image" && (
            <div>
              {categoryDetails.imageURL && (
                <div>
                  <h3>Current Image</h3>
                  <img src={categoryDetails.imageURL} alt="Category" style={{ width: "200px" }} />
                </div>
              )}
              <div>
                <label>Upload New Image:</label>
                <input type="file" accept="image/*" onChange={handleFileChange} />
              </div>
              <div>
                <button type="button" onClick={handleUpdateImage}>
                  Update Image
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    categoryActions: bindActionCreators(ItemGroupsActions, dispatch),
  };
};

export default connect(null, mapDispatchToProps)(EditCategoryPageComponent);
